import React, { useMemo } from 'react';
import { CalendarEvent } from '../../types/calendar';

interface MonthViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  onDateSelect: (date: Date) => void;
}

const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  events,
  onDateSelect
}) => {
  const monthData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return { days, firstDay, lastDay };
  }, [currentDate]);

  const getEventsForDay = (day: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.startTime);
      return eventDate.toDateString() === day.toDateString();
    });
  };

  const isCurrentMonth = (day: Date) => {
    return day.getMonth() === currentDate.getMonth();
  };

  const isToday = (day: Date) => {
    const today = new Date();
    return day.toDateString() === today.toDateString();
  };

  return (
    <div
      className="flex-1 bg-white overflow-hidden"
      style={{ marginTop: '20px' }}
    >
      {/* Week headers */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-white">
        {['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'].map((day, index) => (
          <div
            key={index}
            className="p-3 text-center text-xs font-semibold text-gray-600 uppercase"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-7 h-full">
        {monthData.days.map((day, index) => {
          const dayEvents = getEventsForDay(day);
          const isInCurrentMonth = isCurrentMonth(day);
          const isTodayDate = isToday(day);
          
          return (
            <div
              key={index}
              className={`
                border-r border-b border-gray-200 p-3 cursor-pointer hover:bg-blue-50/20 transition-all min-h-[90px]
                ${!isInCurrentMonth ? 'bg-gray-50/50 text-gray-400' : 'bg-white'}
                ${isTodayDate ? 'bg-blue-50/40 ring-1 ring-blue-200' : ''}
              `}
              onClick={() => onDateSelect(day)}
            >
              <div className="flex items-start justify-between mb-2">
                <div
                  className={`
                    text-sm font-medium w-7 h-7 rounded-full flex items-center justify-center
                    ${isTodayDate ? 'bg-blue-600 text-white' : isInCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                  `}
                >
                  {day.getDate()}
                </div>
              </div>

              <div className="space-y-1">
                {dayEvents.slice(0, 3).map(event => (
                  <div
                    key={event.id}
                    className="w-full h-1.5 rounded-full"
                    style={{
                      backgroundColor: event.color
                    }}
                    title={event.title}
                  />
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-xs text-gray-600 font-medium pt-1">
                    +{dayEvents.length - 3}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthView;